import type {
  GroceryListResponse,
  MealPlan,
  PlanFillResponse,
  PlanTemplate,
  PlanTemplateListItem,
} from '@nutians/shared/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';

export const PLAN_TEMPLATES_KEY = ['plan', 'templates'] as const;
export const planDayKey = (date: string) => ['plan', 'day', date] as const;
export const groceryKey = (from: string, to: string) => ['plan', 'grocery', from, to] as const;

export function usePlanTemplates() {
  return useQuery<{ templates: PlanTemplateListItem[] }>({
    queryKey: PLAN_TEMPLATES_KEY,
    queryFn: async () => {
      const { data, error } = await api.GET('/me/plan/templates', {});
      if (error || !data) throw new Error('GET /me/plan/templates failed');
      return data as { templates: PlanTemplateListItem[] };
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useDayPlan(date: string) {
  return useQuery<{ plan: MealPlan | null }>({
    queryKey: planDayKey(date),
    queryFn: async () => {
      const { data, error } = await api.GET('/me/plan/{date}', {
        params: { path: { date } },
      });
      if (error || !data) throw new Error(`GET /me/plan/${date} failed`);
      return data as unknown as { plan: MealPlan | null };
    },
    staleTime: 30_000,
    retry: false,
  });
}

export function useGroceryList(from: string, to: string, enabled = true) {
  return useQuery<GroceryListResponse>({
    queryKey: groceryKey(from, to),
    queryFn: async () => {
      const { data, error } = await api.GET('/me/plan/grocery', {
        params: { query: { from, to } },
      });
      if (error || !data) throw new Error('GET /me/plan/grocery failed');
      return data as unknown as GroceryListResponse;
    },
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function usePlanFill() {
  const qc = useQueryClient();
  return useMutation<PlanFillResponse, Error, { date: string; mode?: 'deterministic' | 'ai' }>({
    mutationFn: async ({ date, mode = 'deterministic' }) => {
      const { data, error } = await api.POST('/me/plan/fill', {
        params: { query: { mode } },
        body: { date },
      });
      if (error || !data) {
        const errBody = error as { error?: string } | undefined;
        if (errBody?.error === 'BatteryEmpty')
          throw Object.assign(new Error('BatteryEmpty'), { code: 'BatteryEmpty' });
        throw new Error('POST /me/plan/fill failed');
      }
      return data as PlanFillResponse;
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: planDayKey(result.date) });
    },
  });
}

export function useApplyTemplate() {
  const qc = useQueryClient();
  return useMutation<
    { plans: MealPlan[] },
    Error,
    { templateId: string; weekStartDate: string; overwrite?: boolean }
  >({
    mutationFn: async ({ templateId, weekStartDate, overwrite = false }) => {
      const { data, error } = await api.POST('/me/plan/templates/{id}/apply', {
        params: { path: { id: templateId } },
        body: { weekStartDate, overwrite },
      });
      if (error || !data) throw new Error('POST apply template failed');
      return data as { plans: MealPlan[] };
    },
    onSuccess: (result) => {
      for (const plan of result.plans) {
        void qc.invalidateQueries({ queryKey: planDayKey(plan.planDate) });
      }
    },
  });
}

export function usePatchPlanItem() {
  const qc = useQueryClient();
  return useMutation<
    { item: MealPlan['items'][number] },
    Error,
    { date: string; itemId: string; isLogged?: boolean; mealSlot?: string; sortOrder?: number }
  >({
    mutationFn: async ({ date, itemId, ...body }) => {
      const { data, error } = await api.PATCH('/me/plan/{date}/items/{itemId}', {
        params: { path: { date, itemId } },
        body,
      });
      if (error || !data) throw new Error('PATCH plan item failed');
      return data as unknown as { item: MealPlan['items'][number] };
    },
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: planDayKey(vars.date) });
    },
  });
}

export function useDeleteDayPlan() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (date) => {
      const { error } = await api.DELETE('/me/plan/{date}', {
        params: { path: { date } },
      });
      if (error) throw new Error('DELETE plan failed');
    },
    onSuccess: (_, date) => {
      void qc.setQueryData(planDayKey(date), { plan: null });
    },
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation<PlanTemplate, Error, { name: string; description?: string }>({
    mutationFn: async (body) => {
      const { data, error } = await api.POST('/me/plan/templates', {
        body: { ...body, items: [] },
      });
      if (error || !data) throw new Error('POST /me/plan/templates failed');
      return data as unknown as PlanTemplate;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLAN_TEMPLATES_KEY });
    },
  });
}
